import React, {FC, memo, useState} from "react";
import {Alert, Button, Form, Input, message, Modal, Popconfirm, Space, Tag, Typography} from "antd";
import {BookingBillingAddressInput, useGetBookingBillingAddressQuery, useUpdateBookingBillingAddressMutation} from "../../../generated/graphql";
import i18n from "../../../services/i18n";
import PanelCard from "../../../views/PanelCard/PanelCard";

type BookingBillingAddressPanelProps = {
    /** Ohne ID (neue Buchung) zeigt die Komponente nichts: die Adresse hängt an einer gespeicherten Buchung. */
    bookingId?: string | null;
    /** Abgerechnete Buchung: die gespeicherte Rechnungs-PDF ändert sich nicht, der Dialog verweist auf die Korrekturrechnung. */
    settled?: boolean;
};

type FormValues = Omit<BookingBillingAddressInput, "hasBillingAddress">;

/**
 * Abweichende Rechnungsadresse nur dieser Buchung (z. B. aus dem Checkout der End-User-App). Hat auf Rechnung,
 * Mails und Mahnungen Vorrang vor Teilnehmer- und Bucher-Rechnungsadresse (server/invoice/invoiceRecipient.ts).
 */
const BookingBillingAddressPanel: FC<BookingBillingAddressPanelProps> = ({bookingId, settled}) => {
    const tr = i18n.containers.invoice.InvoiceDetails.bookingBillingAddress;
    const fields = i18n.containers.customers.CustomersCreate.steps.bookerContact.billingAddress.fields;
    const validation = i18n.containers.customers.CustomersCreate.steps.bookerContact.billingAddress.validation;
    const [modalOpen, setModalOpen] = useState(false);
    const [form] = Form.useForm<FormValues>();
    // no-cache: dieselbe Wurzel `bookingById` laden die Buchungsansichten mit anderer Feldauswahl. Ohne __typename
    // (addTypename: false) würden sich die Einträge gegenseitig ersetzen und Nachladen auslösen.
    const {data, refetch} = useGetBookingBillingAddressQuery({variables: {id: bookingId ?? ""}, skip: !bookingId, fetchPolicy: "no-cache"});
    const [updateBillingAddress, {loading: saving}] = useUpdateBookingBillingAddressMutation();
    const booking = data?.bookingById;

    if (!bookingId || !booking) {
        return null;
    }

    const save = async (billingAddress: BookingBillingAddressInput, successText: string) => {
        try {
            await updateBillingAddress({variables: {bookingId, billingAddress}, fetchPolicy: "no-cache"});
            await refetch();
            message.success(successText);
            setModalOpen(false);
        } catch (error) {
            message.error(error instanceof Error ? error.message : String(error));
        }
    };

    const openModal = () => {
        form.setFieldsValue({
            billingCompanyName: booking.billingCompanyName,
            billingFirstname: booking.billingFirstname,
            billingLastname: booking.billingLastname,
            billingAddition: booking.billingAddition,
            billingStreet: booking.billingStreet,
            billingPostCode: booking.billingPostCode,
            billingCity: booking.billingCity,
        });
        setModalOpen(true);
    };

    const companyOrLastname = ({getFieldValue}: {getFieldValue: (name: string) => unknown}) => ({
        validator: async () => {
            const filled = (name: string) => String(getFieldValue(name) ?? "").trim().length > 0;

            if (!filled("billingCompanyName") && !filled("billingLastname")) {
                throw new Error(tr.nameOrCompany());
            }
        },
    });
    const companyLabel = fields.companyName.label();
    const firstnameLabel = fields.firstname.label();
    const lastnameLabel = fields.lastname.label();
    const additionLabel = fields.addition.label();
    const streetLabel = fields.street.label();
    const postCodeLabel = fields.postCode.label();
    const cityLabel = fields.city.label();
    const streetRequired = validation.streetRequired();
    const postCodeRequired = validation.postCodeRequired();
    const cityRequired = validation.cityRequired();
    const removeConfirm = tr.removeConfirm();
    const settledHint = tr.settledHint();

    const modal = (
        <Modal
            title={tr.title()}
            visible={modalOpen}
            okText={tr.save()}
            cancelText={tr.cancel()}
            confirmLoading={saving}
            onCancel={() => setModalOpen(false)}
            onOk={async () => {
                let values: FormValues;

                try {
                    values = await form.validateFields();
                } catch {
                    return; // Feldfehler zeigt das Formular selbst
                }
                await save({...values, hasBillingAddress: true}, tr.saved());
            }}
            destroyOnClose
        >
            <Typography.Paragraph type="secondary">{tr.hint()}</Typography.Paragraph>
            {settled && <Alert type="warning" showIcon message={settledHint} style={{marginBottom: 16}} />}
            <Form form={form} layout="vertical" preserve={false}>
                <Form.Item name="billingCompanyName" label={companyLabel} dependencies={["billingLastname"]} rules={[companyOrLastname]}>
                    <Input maxLength={200} />
                </Form.Item>
                <Form.Item name="billingFirstname" label={firstnameLabel}>
                    <Input maxLength={200} />
                </Form.Item>
                <Form.Item name="billingLastname" label={lastnameLabel} dependencies={["billingCompanyName"]} rules={[companyOrLastname]}>
                    <Input maxLength={200} />
                </Form.Item>
                <Form.Item name="billingAddition" label={additionLabel}>
                    <Input maxLength={200} />
                </Form.Item>
                <Form.Item name="billingStreet" label={streetLabel} rules={[{required: true, whitespace: true, message: streetRequired}]}>
                    <Input maxLength={200} />
                </Form.Item>
                <Form.Item name="billingPostCode" label={postCodeLabel} rules={[{required: true, whitespace: true, message: postCodeRequired}]}>
                    <Input maxLength={200} />
                </Form.Item>
                <Form.Item name="billingCity" label={cityLabel} rules={[{required: true, whitespace: true, message: cityRequired}]}>
                    <Input maxLength={200} />
                </Form.Item>
            </Form>
        </Modal>
    );

    if (!booking.hasBillingAddress) {
        return (
            <>
                <Button size="small" onClick={openModal} style={{marginBottom: 10}}>
                    {tr.add()}
                </Button>
                {modal}
            </>
        );
    }

    return (
        <>
            <PanelCard
                title={
                    <Space>
                        {tr.title()}
                        <Tag color="blue">{tr.priority()}</Tag>
                    </Space>
                }
                columns={1}
                style={{fontWeight: 500, borderColor: "lightgrey"}}
                smallTitle
                gapAfter
            >
                {booking.billingCompanyName && <div>{booking.billingCompanyName}</div>}
                {(booking.billingFirstname || booking.billingLastname) && <div>{[booking.billingFirstname, booking.billingLastname].filter(Boolean).join(" ")}</div>}
                {booking.billingAddition && <div>{booking.billingAddition}</div>}
                <div>{booking.billingStreet}</div>
                <div>{[booking.billingPostCode, booking.billingCity].filter(Boolean).join(" ")}</div>
                {/* Buttons unter der Adresse: in der schmalen Spalte des Buchungseditors passt neben Titel und Tag nichts mehr. */}
                <Space style={{marginTop: 8}}>
                    <Button size="small" onClick={openModal}>
                        {tr.edit()}
                    </Button>
                    <Popconfirm title={settled ? `${removeConfirm} ${settledHint}` : removeConfirm} onConfirm={async () => save({hasBillingAddress: false}, tr.removed())}>
                        <Button size="small" danger loading={saving}>
                            {tr.remove()}
                        </Button>
                    </Popconfirm>
                </Space>
            </PanelCard>
            {modal}
        </>
    );
};

export default memo(BookingBillingAddressPanel);
